import React, { useState } from 'react';
import { getPillar } from '../pillars';
import { trackExampleContribution, trackContribution } from '../utils/patternSignals';

/**
 * ContributeExample - Optional contribution after completion
 *
 * Allows learners to share a brief example from their life
 * where this thinking showed up.
 *
 * Design principles:
 * - Optional, never required
 * - Short (280 char max, like early Twitter)
 * - Anonymous by default
 * - Calm invitation, no pressure
 * - Contribution, not performance
 */
function ContributeExample({ levelId, pillarKey, onContribute, onSkip }) {
  const [exampleText, setExampleText] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const pillar = pillarKey ? getPillar(pillarKey) : null;

  const maxLength = 280;
  const remaining = maxLength - exampleText.length;

  const handleSubmit = () => {
    if (exampleText.trim().length === 0) return;

    // Get existing examples
    const storedExamples = localStorage.getItem('sifiso-community-examples');
    const allExamples = storedExamples ? JSON.parse(storedExamples) : [];

    // Add new example
    const newExample = {
      levelId: levelId,
      pillar: pillarKey,
      text: exampleText.trim(),
      timestamp: new Date().toISOString(),
      anonymous: true
    };

    allExamples.push(newExample);
    localStorage.setItem('sifiso-community-examples', JSON.stringify(allExamples));

    // Track pattern: aggregate contribution by pillar and level
    trackExampleContribution(levelId, pillarKey);
    trackContribution(true);

    setIsSubmitted(true);

    if (onContribute) {
      onContribute(newExample);
    }
  };

  const handleSkip = () => {
    // Track that contribution was skipped (aggregate only)
    trackContribution(false);

    if (onSkip) {
      onSkip();
    }
  };

  if (isSubmitted) {
    return (
      <div className="p-6 bg-slate-900/30 rounded-lg border border-green-600/30">
        <div className="text-center">
          <div className="mb-3 text-3xl">🌱</div>
          <p className="text-slate-300 mb-2">
            Thank you for sharing
          </p>
          <p className="text-slate-400 text-sm">
            Your example will help others see this thinking in their own lives
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-slate-900/30 rounded-lg border border-slate-700/30">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          {pillar && <span className="text-xl">{pillar.icon}</span>}
          <h3 className="text-lg font-semibold text-slate-200">
            Share an Example (Optional)
          </h3>
        </div>
        <p className="text-slate-400 text-sm leading-relaxed">
          An example from your life where this thinking showed up
        </p>
      </div>

      {/* Example prompts based on pillar */}
      <div className="mb-4 p-3 bg-slate-800/50 rounded-lg">
        <p className="text-slate-400 text-sm">
          {pillar?.name === 'Problem-Solving Literacy' && "A situation you broke down into smaller steps, or a messy problem you worked through systematically."}
          {pillar?.name === 'Digital Literacy' && "A moment when you understood how a device, app, or system actually worked."}
          {pillar?.name === 'Economic Literacy' && "A decision about money, time, or resources where this thinking helped."}
          {pillar?.name === 'AI Literacy' && "A time you questioned something that sounded confident but might not be correct."}
          {!pillar && "A moment when this thinking showed up in your day."}
        </p>
      </div>

      {/* Text area */}
      <textarea
        value={exampleText}
        onChange={(e) => {
          if (e.target.value.length <= maxLength) {
            setExampleText(e.target.value);
          }
        }}
        placeholder="Brief example (e.g., 'I used this when debugging why my phone wasn't charging—turned out to be a software setting, not the cable.')"
        className="w-full h-24 p-3 bg-slate-900 border border-slate-600 rounded-lg
                   text-slate-200 placeholder-slate-500 text-sm
                   focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500
                   resize-none"
      />

      {/* Character count */}
      <div className="mt-2 flex items-center justify-between text-xs">
        <span className="text-slate-500 italic">
          Shared anonymously with the community
        </span>
        <span className={`${remaining < 20 ? 'text-yellow-400' : 'text-slate-500'}`}>
          {remaining} characters left
        </span>
      </div>

      {/* Actions */}
      <div className="mt-4 flex items-center justify-between">
        <button
          onClick={handleSkip}
          className="px-4 py-2 text-slate-400 hover:text-slate-300 transition text-sm"
        >
          Skip
        </button>

        <button
          onClick={handleSubmit}
          disabled={exampleText.trim().length === 0}
          className={`px-6 py-3 rounded-lg font-semibold transition ${
            exampleText.trim().length === 0
              ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700 text-white'
          }`}
        >
          Share Example
        </button>
      </div>

      {/* Privacy note */}
      <div className="mt-4 pt-4 border-t border-slate-700/30">
        <p className="text-slate-500 text-xs">
          Your example is stored locally and shared anonymously. No personal information is collected.
        </p>
      </div>
    </div>
  );
}

export default ContributeExample;
